const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const EVM_ADDRESS = /^0x[0-9a-fA-F]{40}$/;
const HASH = /^0x[0-9a-fA-F]{64}$/;
const CODE = /^[A-Za-z0-9_-]{43}$/;
const STATE = /^[A-Za-z0-9_-]{43}$/;
const VERIFIER = /^[A-Za-z0-9._~-]{43,128}$/;
const ALLOWED_ROLES = new Set([
  "superadmin",
  "admin",
  "overseer",
  "watcher",
  "spectator",
]);

export const PROMETHEUS_MEMBER_BOOTSTRAP_CLIENT_ID = "prometheus-web";
export const PROMETHEUS_MEMBER_BOOTSTRAP_AUDIENCE = "watcher-member-session";
export const PROMETHEUS_MEMBER_BOOTSTRAP_HQ_ORIGIN =
  "https://watchersociety.com";
export const PROMETHEUS_MEMBER_BOOTSTRAP_ORIGIN =
  "https://app.prometheus.social";
export const PROMETHEUS_MEMBER_BOOTSTRAP_REDIRECT_URI = `${PROMETHEUS_MEMBER_BOOTSTRAP_ORIGIN}/auth/callback`;

const ATTEMPT_KEY = "prometheus.member-bootstrap.attempt.v1";
const SESSION_KEY = "prometheus.member-bootstrap.session.v1";
const ATTEMPT_TTL_MS = 15 * 60 * 1000;
const MAX_STORAGE_BYTES = 12_000;

type SessionStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

type StoredAttempt = {
  version: 1;
  verifier: string;
  state: string;
  startedAt: number;
  expiresAt: number;
};

type StoredSession = {
  version: 1;
  accessToken: string;
  tokenType: "Bearer";
  expiresIn: number;
  obtainedAt: number;
};

export type MemberMigrationLifecycleState = "prepared" | "active" | "suspended";

export type MemberMigrationState = {
  migrated: boolean;
  lifecycleState: MemberMigrationLifecycleState;
  pendingUserOpHash: string | null;
  importedWalletAddress: string | null;
};

export type MemberMigrationMode =
  | "provider-neutral"
  | "legacy-recovery"
  | "blocked";

export type MemberBootstrapSession = {
  token: string;
  canonicalUserId: string;
  canonicalEmail: string;
  canonicalWalletAddress: string;
  role: string;
  exp: number;
  migrationState: MemberMigrationState;
};

type TokenResponse = {
  access_token?: unknown;
  token_type?: unknown;
  expires_in?: unknown;
};

function exactKeys(value: object, expected: readonly string[]): boolean {
  const keys = Object.keys(value).sort();
  return (
    keys.length === expected.length &&
    expected.every((key, index) => key === keys[index])
  );
}

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function decodeJwtSegment(segment: string): Record<string, unknown> {
  try {
    const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(
      normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="),
    );
    const bytes = Uint8Array.from(binary, (character) =>
      character.charCodeAt(0),
    );
    const value = JSON.parse(new TextDecoder().decode(bytes)) as unknown;
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      throw new Error();
    }
    return value as Record<string, unknown>;
  } catch {
    throw new Error("MEMBER_BOOTSTRAP_TOKEN_INVALID");
  }
}

function parseStoredJson(value: string | null): unknown {
  if (!value || value.length > MAX_STORAGE_BYTES) return null;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

function assertNotAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new Error("MEMBER_BOOTSTRAP_CANCELLED");
}

function readStoredAttempt(
  storage: SessionStorage,
  now: number,
): StoredAttempt {
  const value = parseStoredJson(
    storage.getItem(ATTEMPT_KEY),
  ) as Partial<StoredAttempt> | null;
  if (
    !value ||
    typeof value !== "object" ||
    value.version !== 1 ||
    typeof value.verifier !== "string" ||
    !VERIFIER.test(value.verifier) ||
    typeof value.state !== "string" ||
    !STATE.test(value.state) ||
    !Number.isSafeInteger(value.startedAt) ||
    !Number.isSafeInteger(value.expiresAt) ||
    Number(value.expiresAt) - Number(value.startedAt) !== ATTEMPT_TTL_MS ||
    now < Number(value.startedAt) ||
    now > Number(value.expiresAt)
  ) {
    storage.removeItem(ATTEMPT_KEY);
    throw new Error("MEMBER_BOOTSTRAP_ATTEMPT_INVALID");
  }
  return value as StoredAttempt;
}

export function memberBootstrapEnabled(value: string | undefined): boolean {
  return value === "true";
}

export function parseMemberMigrationState(
  value: unknown,
): MemberMigrationState {
  const candidate = value as Partial<MemberMigrationState> | null;
  if (
    !candidate ||
    typeof candidate !== "object" ||
    !exactKeys(candidate, [
      "importedWalletAddress",
      "lifecycleState",
      "migrated",
      "pendingUserOpHash",
    ]) ||
    typeof candidate.migrated !== "boolean" ||
    !["prepared", "active", "suspended"].includes(
      String(candidate.lifecycleState),
    ) ||
    !(
      candidate.pendingUserOpHash === null ||
      (typeof candidate.pendingUserOpHash === "string" &&
        HASH.test(candidate.pendingUserOpHash))
    ) ||
    !(
      (candidate.migrated === true &&
        candidate.lifecycleState === "active" &&
        typeof candidate.importedWalletAddress === "string" &&
        EVM_ADDRESS.test(candidate.importedWalletAddress)) ||
      (candidate.migrated === false &&
        (candidate.lifecycleState === "prepared" ||
          candidate.lifecycleState === "suspended") &&
        candidate.pendingUserOpHash === null &&
        candidate.importedWalletAddress === null)
    )
  ) {
    throw new Error("MEMBER_MIGRATION_STATE_INVALID");
  }
  return candidate as MemberMigrationState;
}

export function memberMigrationMode(
  state: MemberMigrationState | null,
): MemberMigrationMode {
  if (!state || state.pendingUserOpHash !== null) return "blocked";
  if (
    state.migrated === true &&
    state.lifecycleState === "active" &&
    typeof state.importedWalletAddress === "string" &&
    EVM_ADDRESS.test(state.importedWalletAddress)
  ) {
    return "provider-neutral";
  }
  if (
    state.migrated === false &&
    state.importedWalletAddress === null &&
    (state.lifecycleState === "prepared" ||
      state.lifecycleState === "suspended")
  ) {
    return "legacy-recovery";
  }
  return "blocked";
}

export function memberMigrationRecoveryEligible(
  state: MemberMigrationState | null,
): boolean {
  return Boolean(
    state?.migrated === true &&
      state.lifecycleState === "active" &&
      typeof state.importedWalletAddress === "string" &&
      EVM_ADDRESS.test(state.importedWalletAddress) &&
      state.pendingUserOpHash !== null,
  );
}

export function memberMigrationSignerMatches(
  state: MemberMigrationState | null,
  signerAddress: string | null | undefined,
): boolean {
  return Boolean(
    state?.migrated === true &&
      state.lifecycleState === "active" &&
      typeof state.importedWalletAddress === "string" &&
      typeof signerAddress === "string" &&
      EVM_ADDRESS.test(signerAddress) &&
      state.importedWalletAddress.toLowerCase() === signerAddress.toLowerCase(),
  );
}

export function memberMigrationAuthenticationAllowed(
  state: MemberMigrationState | null,
): boolean {
  return (
    memberMigrationMode(state) !== "blocked" ||
    memberMigrationRecoveryEligible(state)
  );
}

export function parseMemberBootstrapToken(
  response: TokenResponse,
  now: number,
): Omit<MemberBootstrapSession, "migrationState"> {
  if (
    !response ||
    typeof response !== "object" ||
    !exactKeys(response, ["access_token", "expires_in", "token_type"]) ||
    typeof response.access_token !== "string" ||
    response.access_token.length > 8192 ||
    !JWT_SHAPE.test(response.access_token) ||
    response.token_type !== "Bearer" ||
    !Number.isSafeInteger(response.expires_in) ||
    Number(response.expires_in) < 1 ||
    Number(response.expires_in) > 14_400
  ) {
    throw new Error("MEMBER_BOOTSTRAP_TOKEN_INVALID");
  }
  const segments = response.access_token.split(".");
  const header = decodeJwtSegment(segments[0] ?? "");
  const payload = decodeJwtSegment(segments[1] ?? "");
  const issuedAt = payload.iat;
  const expiresAt = payload.exp;
  const emailHasControl =
    typeof payload.canonicalEmail === "string" &&
    Array.from(payload.canonicalEmail).some((character) => {
      const code = character.charCodeAt(0);
      return code <= 31 || code === 127;
    });
  if (
    header.alg !== "HS256" ||
    payload.iss !== "gastown-api" ||
    payload.aud !== "watcher-hq-member-session" ||
    payload.authMethod !== "better_auth" ||
    payload.principalScope !== "member_session" ||
    typeof payload.canonicalUserId !== "string" ||
    payload.canonicalUserId.length < 1 ||
    payload.canonicalUserId.length > 128 ||
    typeof payload.canonicalEmail !== "string" ||
    payload.canonicalEmail.length < 3 ||
    payload.canonicalEmail.length > 320 ||
    !payload.canonicalEmail.includes("@") ||
    emailHasControl ||
    typeof payload.canonicalWalletAddress !== "string" ||
    !EVM_ADDRESS.test(payload.canonicalWalletAddress) ||
    typeof payload.role !== "string" ||
    !ALLOWED_ROLES.has(payload.role) ||
    !Number.isSafeInteger(issuedAt) ||
    !Number.isSafeInteger(expiresAt) ||
    Number(expiresAt) <= Math.floor(now / 1000) ||
    Number(expiresAt) - Number(issuedAt) !== Number(response.expires_in)
  ) {
    throw new Error("MEMBER_BOOTSTRAP_TOKEN_INVALID");
  }
  return {
    token: response.access_token,
    canonicalUserId: payload.canonicalUserId,
    canonicalEmail: payload.canonicalEmail,
    canonicalWalletAddress: payload.canonicalWalletAddress,
    role: payload.role,
    exp: Number(expiresAt) * 1000,
  };
}

function parseCallback(callbackUrl: string, expectedState: string) {
  let url: URL;
  try {
    url = new URL(callbackUrl);
  } catch {
    throw new Error("MEMBER_BOOTSTRAP_CALLBACK_INVALID");
  }
  const expected = new URL(PROMETHEUS_MEMBER_BOOTSTRAP_REDIRECT_URI);
  if (
    url.origin !== expected.origin ||
    url.pathname !== expected.pathname ||
    url.username ||
    url.password ||
    url.hash ||
    [...url.searchParams.keys()].some(
      (key) => key !== "code" && key !== "state",
    ) ||
    url.searchParams.getAll("code").length !== 1 ||
    url.searchParams.getAll("state").length !== 1
  ) {
    throw new Error("MEMBER_BOOTSTRAP_CALLBACK_INVALID");
  }
  const code = url.searchParams.get("code") ?? "";
  const state = url.searchParams.get("state") ?? "";
  if (!CODE.test(code) || !STATE.test(state) || state !== expectedState) {
    throw new Error("MEMBER_BOOTSTRAP_CALLBACK_INVALID");
  }
  return { code, state };
}

export async function createMemberBootstrapAuthorizeUrl(options: {
  storage: SessionStorage;
  origin: string;
  now?: number;
  cryptoImpl?: Crypto;
}): Promise<string> {
  if (options.origin !== PROMETHEUS_MEMBER_BOOTSTRAP_ORIGIN) {
    throw new Error("MEMBER_BOOTSTRAP_ORIGIN_INVALID");
  }
  const cryptoImpl = options.cryptoImpl ?? crypto;
  const verifierBytes = new Uint8Array(32);
  const stateBytes = new Uint8Array(32);
  cryptoImpl.getRandomValues(verifierBytes);
  cryptoImpl.getRandomValues(stateBytes);
  const verifier = base64Url(verifierBytes);
  const state = base64Url(stateBytes);
  const challenge = base64Url(
    new Uint8Array(
      await cryptoImpl.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(verifier).buffer as ArrayBuffer,
      ),
    ),
  );
  if (!VERIFIER.test(verifier) || !STATE.test(state) || !CODE.test(challenge)) {
    throw new Error("MEMBER_BOOTSTRAP_RANDOMNESS_INVALID");
  }
  const now = options.now ?? Date.now();
  const attempt: StoredAttempt = {
    version: 1,
    verifier,
    state,
    startedAt: now,
    expiresAt: now + ATTEMPT_TTL_MS,
  };
  options.storage.setItem(ATTEMPT_KEY, JSON.stringify(attempt));
  const authorize = new URL(
    "/members/auth/authorize",
    PROMETHEUS_MEMBER_BOOTSTRAP_HQ_ORIGIN,
  );
  authorize.search = new URLSearchParams({
    response_type: "code",
    client_id: PROMETHEUS_MEMBER_BOOTSTRAP_CLIENT_ID,
    redirect_uri: PROMETHEUS_MEMBER_BOOTSTRAP_REDIRECT_URI,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
    audience: PROMETHEUS_MEMBER_BOOTSTRAP_AUDIENCE,
  }).toString();
  return authorize.toString();
}

async function exchangeMemberBootstrapCode(options: {
  callbackUrl: string;
  storage: SessionStorage;
  fetchImpl: typeof fetch;
  now: number;
  signal?: AbortSignal;
}): Promise<Omit<MemberBootstrapSession, "migrationState">> {
  const attempt = readStoredAttempt(options.storage, options.now);
  const callback = parseCallback(options.callbackUrl, attempt.state);
  options.storage.removeItem(ATTEMPT_KEY);
  const tokenUrl = `${PROMETHEUS_MEMBER_BOOTSTRAP_HQ_ORIGIN}/api/member/bootstrap/token`;
  const preflight = await options.fetchImpl(
    `${tokenUrl}?client_id=${PROMETHEUS_MEMBER_BOOTSTRAP_CLIENT_ID}`,
    {
      method: "OPTIONS",
      credentials: "omit",
      cache: "no-store",
      signal: options.signal,
    },
  );
  assertNotAborted(options.signal);
  if (preflight.status !== 204) {
    throw new Error("MEMBER_BOOTSTRAP_EXCHANGE_UNAVAILABLE");
  }
  const response = await options.fetchImpl(tokenUrl, {
    method: "POST",
    credentials: "omit",
    cache: "no-store",
    signal: options.signal,
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: PROMETHEUS_MEMBER_BOOTSTRAP_CLIENT_ID,
      redirect_uri: PROMETHEUS_MEMBER_BOOTSTRAP_REDIRECT_URI,
      state: callback.state,
      audience: PROMETHEUS_MEMBER_BOOTSTRAP_AUDIENCE,
      code: callback.code,
      code_verifier: attempt.verifier,
    }).toString(),
  });
  assertNotAborted(options.signal);
  if (!response.ok) throw new Error("MEMBER_BOOTSTRAP_EXCHANGE_UNAVAILABLE");
  const contentType = response.headers.get("content-type")?.split(";")[0];
  if (contentType !== "application/json") {
    throw new Error("MEMBER_BOOTSTRAP_EXCHANGE_INVALID");
  }
  const body = (await response.json()) as TokenResponse;
  assertNotAborted(options.signal);
  const session = parseMemberBootstrapToken(body, options.now);
  const stored: StoredSession = {
    version: 1,
    accessToken: session.token,
    tokenType: "Bearer",
    expiresIn: Number(body.expires_in),
    obtainedAt: options.now,
  };
  assertNotAborted(options.signal);
  options.storage.setItem(SESSION_KEY, JSON.stringify(stored));
  return session;
}

export async function completeMemberBootstrap(options: {
  callbackUrl: string;
  storage: SessionStorage;
  readMigrationState: (token: string, signal?: AbortSignal) => Promise<unknown>;
  fetchImpl?: typeof fetch;
  now?: number;
  signal?: AbortSignal;
}): Promise<MemberBootstrapSession> {
  try {
    const now = options.now ?? Date.now();
    const session = await exchangeMemberBootstrapCode({
      callbackUrl: options.callbackUrl,
      storage: options.storage,
      fetchImpl: options.fetchImpl ?? fetch,
      now,
      signal: options.signal,
    });
    assertNotAborted(options.signal);
    const migrationState = parseMemberMigrationState(
      await options.readMigrationState(session.token, options.signal),
    );
    assertNotAborted(options.signal);
    if (!memberMigrationAuthenticationAllowed(migrationState)) {
      throw new Error("MEMBER_MIGRATION_PROVIDER_UNAVAILABLE");
    }
    return { ...session, migrationState };
  } catch (error) {
    options.storage.removeItem(ATTEMPT_KEY);
    options.storage.removeItem(SESSION_KEY);
    throw error;
  }
}

export function restoreMemberBootstrapSession(options: {
  storage: SessionStorage;
  now?: number;
}): Omit<MemberBootstrapSession, "migrationState"> | null {
  const value = parseStoredJson(
    options.storage.getItem(SESSION_KEY),
  ) as Partial<StoredSession> | null;
  if (!value) return null;
  try {
    if (
      typeof value !== "object" ||
      value.version !== 1 ||
      typeof value.accessToken !== "string" ||
      value.tokenType !== "Bearer" ||
      !Number.isSafeInteger(value.expiresIn) ||
      !Number.isSafeInteger(value.obtainedAt)
    ) {
      throw new Error();
    }
    return parseMemberBootstrapToken(
      {
        access_token: value.accessToken,
        token_type: value.tokenType,
        expires_in: value.expiresIn,
      },
      options.now ?? Date.now(),
    );
  } catch {
    options.storage.removeItem(SESSION_KEY);
    return null;
  }
}

export function clearMemberBootstrapStorage(storage: SessionStorage): void {
  storage.removeItem(ATTEMPT_KEY);
  storage.removeItem(SESSION_KEY);
}
